import { useState, useRef } from "react";
import { Upload, Link, Eye, AlertCircle, CheckCircle, X, ImageIcon } from "lucide-react";
import styles from "./ImageReplacementEditor.module.css";

export default function ImageReplacementEditor() {
    const [imageConfig, setImageConfig] = useState({
        name: 'Product Image',
        selector: 'img.product-image',
        pageUrl: 'https://example.com/product'
    });

    const [replacementData, setReplacementData] = useState({
        original: {
            imageUrl: '',
            imageAlt: ''
        },
        test: {
            imageUrl: '',
            uploadMethod: 'url',
            originalUploadUrl: null
        }
    });

    const [imageUrl, setImageUrl] = useState('');
    const [uploadMode, setUploadMode] = useState('url');
    const [uploading, setUploading] = useState(false);
    const [localErrors, setLocalErrors] = useState({});
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
    const fileInputRef = useRef(null);

    const validateImageUrl = (url) => {
        if (!url.trim()) {
            return 'Image URL is required';
        }

        try {
            new URL(url);
            return null;
        } catch (error) {
            return 'Invalid URL format';
        }
    };

    const handleImageUrlChange = (value) => {
        setImageUrl(value);

        if (value.trim()) {
            const error = validateImageUrl(value);
            setLocalErrors(prev => ({ ...prev, imageUrl: error }));
        } else {
            setLocalErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.imageUrl;
                return newErrors;
            });
        }

        setUploadedImageUrl(null);
        setImagePreview(null);
    };

    const handleFileSelect = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setLocalErrors(prev => ({
                ...prev,
                upload: 'Please select a valid image file (PNG, JPG, JPEG, GIF, WebP)'
            }));
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setLocalErrors(prev => ({
                ...prev,
                upload: 'Image file must be smaller than 5MB'
            }));
            return;
        }

        setLocalErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.upload;
            return newErrors;
        });

        const reader = new FileReader();
        reader.onload = (e) => {
            setImagePreview({
                src: e.target.result,
                name: file.name,
                size: file.size
            });
        };
        reader.readAsDataURL(file);

        await uploadImageToServer(file);
    };

    const uploadImageToServer = async (file) => {
        setUploading(true);

        try {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('purpose', 'ab_test_image');

            const response = await fetch('/api/upload-image.v2', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to upload image');
            }

            const result = await response.json();

            if (result.success && result.imageUrl) {
                setUploadedImageUrl(result.imageUrl);
                setImageUrl(result.imageUrl);
                setLocalErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.upload;
                    delete newErrors.imageUrl;
                    return newErrors;
                });
                showToast('✅ Image uploaded successfully!');
            } else {
                throw new Error(result.error || 'Upload failed');
            }

        } catch (error) {
            console.error('Upload error:', error);
            setLocalErrors(prev => ({
                ...prev,
                upload: `Upload failed: ${error.message}`
            }));
        } finally {
            setUploading(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        const imageFile = files.find(file => file.type.startsWith('image/'));

        if (imageFile && fileInputRef.current) {
            const dt = new DataTransfer();
            dt.items.add(imageFile);
            fileInputRef.current.files = dt.files;

            handleFileSelect({ target: { files: dt.files } });
        }
    };

    const hasValidInput = () => {
        const urlValid = imageUrl.trim().length > 0 && !localErrors.imageUrl && !localErrors.upload;
        return urlValid && !uploading;
    };

    const handleSubmit = () => {
        const newErrors = {};

        const urlError = validateImageUrl(imageUrl);
        if (urlError) {
            newErrors.imageUrl = urlError;
        }

        setLocalErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            const replacementConfig = {
                original: replacementData.original || {
                    imageUrl: '',
                    imageAlt: ''
                },
                test: {
                    imageUrl: imageUrl.trim(),
                    uploadMethod: uploadedImageUrl ? 'upload' : 'url',
                    originalUploadUrl: uploadedImageUrl || null
                }
            };

            setReplacementData(replacementConfig);
            showToast('✅ Image replacement applied!');
        }
    };

    const openImagePreview = () => {
        if (!imageUrl) return;

        const previewParams = new URLSearchParams({
            cf_image_preview: 'true',
            test_id: Date.now(),
            image_selector: imageConfig.selector,
            image_name: imageConfig.name,
            new_image_url: imageUrl,
            return_url: window.location.href
        });

        const url = new URL(imageConfig.pageUrl);
        previewParams.forEach((value, key) => {
            url.searchParams.set(key, value);
        });

        const previewUrl = url.toString();

        window.open(previewUrl, '_blank', 'noopener,noreferrer');

        showToast('🔍 Preview opened! Check the new tab to see your image replacement.');
    };

    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #10b981;
            color: white;
            padding: 12px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 10000;
        `;

        document.body.appendChild(toast);

        setTimeout(() => {
            document.body.removeChild(toast);
        }, 3000);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    if (!imageConfig) {
        return (
            <div className={styles.container}>
                <div className={styles.emptyState}>
                    <AlertCircle size={48} className={styles.emptyIcon} />
                    <h4>No Image Selected</h4>
                    <p>Please select an image first.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h4 className={styles.title}>Image Replacement Configuration</h4>
                <p className={styles.description}>
                    Choose a replacement image for <strong>{imageConfig.name}</strong>.
                </p>
            </div>

            <div className={styles.form}>
                {/* Current Image Info */}
                <div className={styles.currentImage}>
                    <h5 className={styles.currentImageTitle}>Image to Replace:</h5>
                    <div className={styles.imageInfo}>
                        <span className={styles.imageName}>{imageConfig.name}</span>
                        <code className={styles.imageSelector}>{imageConfig.selector}</code>
                    </div>
                </div>

                {/* Upload Mode Toggle */}
                <div className={styles.inputGroup}>
                    <label className={styles.label}>Replacement Method</label>
                    <div className={styles.modeToggle}>
                        <button
                            type="button"
                            onClick={() => setUploadMode('url')}
                            className={`${styles.modeButton} ${uploadMode === 'url' ? styles.active : ''}`}
                        >
                            <Link size={16} />
                            Image URL
                        </button>
                        <button
                            type="button"
                            onClick={() => setUploadMode('upload')}
                            className={`${styles.modeButton} ${uploadMode === 'upload' ? styles.active : ''}`}
                        >
                            <Upload size={16} />
                            Upload Image
                        </button>
                    </div>
                </div>

                {/* URL Input */}
                {uploadMode === 'url' && (
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>
                            Image URL <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="url"
                            value={imageUrl}
                            onChange={(e) => handleImageUrlChange(e.target.value)}
                            placeholder="https://example.com/image.jpg"
                            className={`${styles.input} ${localErrors.imageUrl ? styles.error : ''}`}
                        />
                        {localErrors.imageUrl && (
                            <div className={styles.errorMessage}>
                                <AlertCircle size={14} />
                                <span>{localErrors.imageUrl}</span>
                            </div>
                        )}
                        <div className={styles.inputHelp}>
                            Enter the URL of the image you want to use as replacement
                        </div>
                    </div>
                )}

                {/* File Upload */}
                {uploadMode === 'upload' && (
                    <div className={styles.inputGroup}>
                        <label className={styles.label}>
                            Upload Image <span className={styles.required}>*</span>
                        </label>

                        <div
                            className={`${styles.uploadArea} ${uploading ? styles.uploading : ''} ${localErrors.upload ? styles.error : ''}`}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className={styles.hiddenInput}
                            />

                            {uploading ? (
                                <div className={styles.uploadingState}>
                                    <div className={styles.spinner}></div>
                                    <span>Uploading...</span>
                                </div>
                            ) : imagePreview ? (
                                <div className={styles.previewState}>
                                    <img
                                        src={imagePreview.src}
                                        alt="Preview"
                                        className={styles.previewImage}
                                    />
                                    <div className={styles.previewInfo}>
                                        <strong>{imagePreview.name}</strong>
                                        <span>{formatFileSize(imagePreview.size)}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setImagePreview(null);
                                            setImageUrl('');
                                            setUploadedImageUrl(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        className={styles.removeButton}
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.uploadPrompt}>
                                    <Upload size={32} />
                                    <div className={styles.uploadText}>
                                        <strong>Click to upload</strong> or drag and drop
                                    </div>
                                    <div className={styles.uploadHint}>
                                        PNG, JPG, JPEG, GIF, WebP (max 5MB)
                                    </div>
                                </div>
                            )}
                        </div>

                        {localErrors.upload && (
                            <div className={styles.errorMessage}>
                                <AlertCircle size={14} />
                                <span>{localErrors.upload}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Image Preview (for URL mode) */}
                {uploadMode === 'url' && imageUrl && !localErrors.imageUrl && (
                    <div className={styles.urlPreview}>
                        <div className={styles.urlPreviewHeader}>
                            <ImageIcon size={16} />
                            <span>Image Preview</span>
                        </div>
                        <img
                            src={imageUrl}
                            alt="URL Preview"
                            className={styles.urlPreviewImage}
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                        <div className={styles.urlPreviewError} style={{ display: 'none' }}>
                            <AlertCircle size={16} />
                            <span>Could not load image from URL</span>
                        </div>
                    </div>
                )}

                {/* Actions */}
                <div className={styles.actions}>
                    {hasValidInput() && (
                        <button
                            onClick={openImagePreview}
                            className={styles.previewButton}
                            title="Preview how the new image will look"
                        >
                            <Eye size={16} />
                            Preview Changes
                        </button>
                    )}
                    <button
                        onClick={handleSubmit}
                        disabled={!hasValidInput()}
                        className={styles.submitButton}
                    >
                        <CheckCircle size={16} />
                        Apply Image Replacement
                    </button>
                </div>
            </div>
        </div>
    );
}